import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  ThumbUp,
  ThumbDown,
  Refresh,
  ContentCopy,
  Share,
  Bookmark,
  BookmarkBorder,
  TrendingUp,
  Insights,
  Psychology,
  Business,
  TableChart
} from '@mui/icons-material';
import { ChatMessage, InterviewSummary } from '../types';

interface EnhancedChatBubbleProps {
  message: ChatMessage;
  isUser: boolean;
  onRetry?: () => void;
  onFeedback?: (type: 'positive' | 'negative') => void;
  onCopy?: () => void;
  onShare?: () => void;
  onBookmark?: () => void;
  interviewKnowledge?: InterviewSummary | null;
  showMetadata?: boolean;
}

interface KnowledgeInsight {
  type: 'table' | 'relationship' | 'business_rule' | 'pattern';
  title: string;
  description: string;
  confidence: number;
  source: string;
}

export function EnhancedChatBubble({
  message,
  isUser,
  onRetry,
  onFeedback,
  onCopy,
  onShare,
  onBookmark,
  interviewKnowledge,
  showMetadata = true
}: EnhancedChatBubbleProps) {
  const [expanded, setExpanded] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(null);

  const generateKnowledgeInsights = (): KnowledgeInsight[] => {
    const insights: KnowledgeInsight[] = [];
    
    if (interviewKnowledge) {
      // Table insights
      if (message.text.toLowerCase().includes('table') || message.text.toLowerCase().includes('data')) {
        interviewKnowledge.tablesDiscovered.forEach(table => {
          insights.push({
            type: 'table',
            title: `Table: ${table}`,
            description: `I understand this table structure from our interview`,
            confidence: 0.9,
            source: 'Interview Knowledge'
          });
        });
      }

      // Relationship insights
      if (message.text.toLowerCase().includes('relationship') || message.text.toLowerCase().includes('join')) {
        interviewKnowledge.relationshipsLearned.forEach(rel => {
          insights.push({
            type: 'relationship',
            title: `Relationship: ${rel}`,
            description: `I learned this connection during our interview`,
            confidence: 0.85,
            source: 'Interview Knowledge'
          });
        });
      }

      // Business rule insights
      if (message.text.toLowerCase().includes('rule') || message.text.toLowerCase().includes('policy')) {
        interviewKnowledge.businessRules.forEach(rule => {
          insights.push({
            type: 'business_rule',
            title: `Business Rule: ${rule}`,
            description: `I understand this business logic`,
            confidence: 0.8,
            source: 'Interview Knowledge'
          });
        });
      }
    }

    // Pattern recognition insights
    if (message.text.toLowerCase().includes('revenue') || message.text.toLowerCase().includes('sales')) {
      insights.push({
        type: 'pattern',
        title: 'Revenue Analysis Pattern',
        description: 'I recognize this as a revenue-related query pattern',
        confidence: 0.75,
        source: 'Query Pattern Recognition'
      });
    }

    if (message.text.toLowerCase().includes('inventory') || message.text.toLowerCase().includes('stock')) {
      insights.push({
        type: 'pattern',
        title: 'Inventory Management Pattern',
        description: 'This matches inventory analysis patterns I\'ve learned',
        confidence: 0.7,
        source: 'Query Pattern Recognition'
      });
    }

    return insights;
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'table': return <TableChart />;
      case 'relationship': return <TrendingUp />;
      case 'business_rule': return <Business />;
      case 'pattern': return <Insights />;
      default: return <Psychology />;
    }
  };

  const getInsightColor = (type: string) => {
    switch (type) {
      case 'table': return 'primary';
      case 'relationship': return 'success';
      case 'business_rule': return 'warning';
      case 'pattern': return 'info';
      default: return 'default';
    }
  };

  const handleFeedback = (type: 'positive' | 'negative') => {
    setFeedback(type);
    onFeedback?.(type);
  };

  const handleBookmark = () => {
    setBookmarked(!bookmarked);
    onBookmark?.();
  };

  const knowledgeInsights = generateKnowledgeInsights();

  return (
    <Box
      sx={{
        mb: 2,
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        flexDirection: 'column'
      }}
    >
      {/* Message Bubble */}
      <Paper
        sx={{
          p: 2,
          maxWidth: '75%',
          backgroundColor: isUser 
            ? 'primary.main' 
            : message.isError 
              ? '#ffebee' 
              : 'background.paper',
          color: isUser 
            ? 'primary.contrastText' 
            : message.isError 
              ? '#c62828' 
              : 'text.primary',
          border: message.isError ? '1px solid #ef5350' : 'none',
          borderRadius: 2,
          boxShadow: 2,
          position: 'relative'
        }}
      >
        {/* Message Content */}
        <Typography variant="body1" component="div" sx={{ mb: 1 }}>
          {message.text}
        </Typography>

        {/* Knowledge Insights */}
        {!isUser && knowledgeInsights.length > 0 && (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Psychology sx={{ mr: 0.5, fontSize: '0.8rem' }} />
              AI Knowledge Applied ({knowledgeInsights.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {knowledgeInsights.slice(0, 3).map((insight, index) => (
                <Chip
                  key={index}
                  label={insight.title}
                  size="small"
                  icon={getInsightIcon(insight.type)}
                  color={getInsightColor(insight.type) as any}
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
              {knowledgeInsights.length > 3 && (
                <Chip
                  label={`+${knowledgeInsights.length - 3} more`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              )}
            </Box>
          </Box>
        )}

        {/* Action Bar */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          mt: 1,
          pt: 1,
          borderTop: 1,
          borderColor: 'divider',
          opacity: 0.7
        }}>
          <Typography variant="caption">
            {message.timestamp ? new Date(message.timestamp).toLocaleTimeString() : ''}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Feedback Buttons */}
            {!isUser && (
              <>
                <Tooltip title="This response was helpful">
                  <IconButton
                    size="small"
                    onClick={() => handleFeedback('positive')}
                    color={feedback === 'positive' ? 'success' : 'default'}
                    sx={{ p: 0.5 }}
                  >
                    <ThumbUp fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="This response needs improvement">
                  <IconButton
                    size="small"
                    onClick={() => handleFeedback('negative')}
                    color={feedback === 'negative' ? 'error' : 'default'}
                    sx={{ p: 0.5 }}
                  >
                    <ThumbDown fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}

            {/* Copy Button */}
            <Tooltip title="Copy message">
              <IconButton size="small" onClick={onCopy} sx={{ p: 0.5 }}>
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Bookmark Button */}
            <Tooltip title={bookmarked ? 'Remove bookmark' : 'Bookmark message'}>
              <IconButton size="small" onClick={handleBookmark} sx={{ p: 0.5 }}>
                {bookmarked ? <Bookmark fontSize="small" color="primary" /> : <BookmarkBorder fontSize="small" />}
              </IconButton>
            </Tooltip>

            {/* Share Button */}
            <Tooltip title="Share message">
              <IconButton size="small" onClick={onShare} sx={{ p: 0.5 }}>
                <Share fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* Expand/Collapse Button */}
            {showMetadata && (message.data || message.sql || message.insights) && (
              <Tooltip title={expanded ? 'Show less' : 'Show more'}>
                <IconButton
                  size="small"
                  onClick={() => setExpanded(!expanded)}
                  sx={{ p: 0.5 }}
                >
                  {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}

            {/* Retry Button */}
            {message.isError && onRetry && (
              <Tooltip title="Retry this query">
                <IconButton size="small" onClick={onRetry} sx={{ p: 0.5 }}>
                  <Refresh fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Expanded Metadata */}
      {showMetadata && expanded && (message.data || message.sql || message.insights) && (
        <Paper sx={{ mt: 1, p: 2, maxWidth: '75%', backgroundColor: 'background.default' }}>
          <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <Insights sx={{ mr: 1, fontSize: '1rem' }} />
            Response Details
          </Typography>

          {/* Knowledge Confidence */}
          {knowledgeInsights.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                Knowledge Confidence: {Math.round(knowledgeInsights[0]?.confidence * 100)}%
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={knowledgeInsights[0]?.confidence * 100 || 0} 
                sx={{ height: 6, borderRadius: 3 }}
                color="primary"
              />
            </Box>
          )}

          {/* SQL Query */}
          {message.sql && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                SQL Query Generated:
              </Typography>
              <Paper sx={{ p: 1, mt: 0.5, backgroundColor: '#f5f5f5' }}>
                <code style={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{message.sql}</code>
              </Paper>
            </Box>
          )}

          {/* Data Results */}
          {message.data && message.data.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Results: {message.data.length} rows
              </Typography>
              <Paper sx={{ p: 1, mt: 0.5, backgroundColor: '#f5f5f5', maxHeight: 200, overflow: 'auto' }}>
                <pre style={{ fontSize: '0.7rem', margin: 0 }}>
                  {JSON.stringify(message.data.slice(0, 3), null, 2)}
                  {message.data.length > 3 && `\n... and ${message.data.length - 3} more rows`}
                </pre>
              </Paper>
            </Box>
          )}

          {/* Insights */}
          {message.insights && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                AI Insights:
              </Typography>
              <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                {message.insights}
              </Typography>
            </Box>
          )}

          {/* Knowledge Sources */}
          {knowledgeInsights.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" gutterBottom>
                Knowledge Sources:
              </Typography>
              <List dense>
                {knowledgeInsights.map((insight, index) => (
                  <ListItem key={index} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {getInsightIcon(insight.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={insight.title}
                      secondary={`${insight.description} (${Math.round(insight.confidence * 100)}% confidence)`}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}
